import { safeMoneyFromCents } from '@libs/services/money';
import type { Cart } from 'src/trpc';
import type { CurrentOrderPricing } from 'components/orders/Details/useOrderCurrentPricing';

// Extract LineItems type from Cart
type CartLineItems = NonNullable<Cart['lineItems']>;

interface OrderTotalSummaryProps {
  lineItems?: CartLineItems;
  pricing: CurrentOrderPricing;
}

export const OrderTotalSummary = ({ lineItems, pricing }: OrderTotalSummaryProps) => {
  if (!lineItems) {
    return null;
  }

  const hasDiscount = pricing.discountInCents > 0;
  const hasOutstandingBalance = (pricing.outstandingBalanceInCents || 0) > 0;
  const taxUnavailable =
    pricing.isTaxLoading || pricing.isTaxError || pricing.taxInCents === undefined;

  return (
    <div>
      <div className="flex flex-col gap-2 bg-slate-100 rounded-lg px-4 py-2 mt-2">
        {/* Subtotal */}
        <div className="flex flex-row w-full justify-between text-sm">
          <p>Subtotal</p>
          <p>{safeMoneyFromCents(pricing.subtotalInCents).toFormat()}</p>
        </div>

        {/* Discount */}
        {hasDiscount && (
          <div className="flex flex-row w-full justify-between text-sm">
            <p className="text-success">{pricing.discountLabel}</p>
            <p className="text-success">
              -{safeMoneyFromCents(pricing.discountInCents).toFormat()}
            </p>
          </div>
        )}

        {/* Tax */}
        <div className="flex flex-row w-full justify-between text-sm">
          <p>Tax</p>
          <p>
            {pricing.isTaxLoading
              ? 'Calculating...'
              : pricing.isTaxError || pricing.taxInCents === undefined
                ? 'Unavailable'
                : safeMoneyFromCents(pricing.taxInCents).toFormat()}
          </p>
        </div>
      </div>

      {/* Total */}
      <div className="bg-slate-800 px-4 py-3 rounded-b-lg flex flex-col gap-2">
        {/* Total Paid - always shown */}
        <div className="flex flex-row justify-between w-full">
          <p className="text-white font-bold">Total Paid</p>
          <p className="font-bold text-white">
            {safeMoneyFromCents(pricing.totalPaymentsInCents).toFormat()}
          </p>
        </div>

        {/* Outstanding Balance - only shown when > 0 */}
        {!taxUnavailable && hasOutstandingBalance && (
          <div className="flex flex-row justify-between w-full">
            <div className="flex flex-row gap-2 items-center">
              <p className="text-white font-bold">Outstanding Balance</p>
            </div>
            <p className="font-bold text-white">
              {safeMoneyFromCents(pricing.outstandingBalanceInCents).toFormat()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
